"use client";

import React from "react";
import {
  Box,
  Button,
  RenderIf,
  Slider,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@repo/ui";
import { ChevronLeftOutline, ChevronRightOutline } from "@spoctech/icons";

import StartLearningWithSpoclearnLoader from "@/components/loaders/StartLearningWithSpoclearnLoader";
import Section from "@/components/Section";

import {
  GET_SERVICE_BY_TYPE,
  ServiceType,
} from "../graphql/queries/service-by-type-query";
import { useLazyQuery } from "@apollo/client/react";
import { useParams } from "next/navigation";
import CourseCard2 from "@/components/courses/CourseCard2";
import { getCookie } from "cookies-next/client";

type CourseSliderProps = {
  data?: ServiceByTypeInterface[];
  tag?: "Popular Course" | "Certification Program" | "Skill-based Course";
};
const Courses = ({ data, tag }: CourseSliderProps) => {
  return (
    <Slider
      id="responsive-slider"
      removeContainerPadding={false}
      removeSliderPadding
      options={{
        breakpoints: {
          "0": {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          "640": {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          "768": {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          "1024": {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          "1170": {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        },
        navigation: {
          enabled: true,
        },
        pagination: {
          clickable: true,
        },
        slidesPerGroup: 1,
        slidesPerView: 1,
        spaceBetween: 20,
        autoplay: false,
        speed: 2000,
      }}
      hideNavigation
    >
      {data?.map((item) => (
        <CourseCard2
          tag={tag || "Popular"}
          key={item.id}
          serviceId={item.id}
          title={item.name}
          coverSrc={item.service_image}
          totalEnroll={item.numberOfLearners}
          duration={item.serviceTrainingDays}
          slug={item.slug}
          accreditation_name={item.accreditation_name}
          icon={item.serviceIcon}
          modeOfTraining={item.mode_of_training || "Live Classroom / Classroom"}
          currency={item.currency}
          sellingPriceNumber={item.price}
          imageLoading="lazy"
        />
      ))}
    </Slider>
  );
};
type StartLearnWithSpoclearnProps = {
  sectionInfo: PageSections;
  data?: {
    heading: string;
    buttonText: string;
    buttonAction: string;
    startLearningWithSpoclearn: ServiceByTypeInterface[];
  };
};

const StartLearnWithSpoclearn = ({
  data,
  sectionInfo,
}: StartLearnWithSpoclearnProps) => {
  const { country: countryParam } = useParams<{ country: string }>();
  const gioCountry = getCookie("location");

  const [getServiceByType, { data: clientData, loading }] =
    useLazyQuery(GET_SERVICE_BY_TYPE);
  const handleTabChange = (value: number) => {
    if (value > 1) {
      getServiceByType({
        variables: {
          serviceType: ServiceType[value],
          countryCode: countryParam || gioCountry,
        },
      });
    }
  };

  return (
    <Section
      title={data?.heading}
      actionLabel={data?.buttonText}
      path={`${countryParam ? `/${countryParam}/${data?.buttonAction}` : data?.buttonAction}`}
      id={sectionInfo.sectionId}
      className={`${sectionInfo.sectionClass} bg-gradient-to-b from-white to-blue-50`}
    >
      <Tabs defaultValue={1} onValueChange={handleTabChange}>
        <Box
          alignItems="center"
          className="sm:flex sm:justify-between sm:flex-row flex-col"
        >
          <TabList className="gray-text" name="start-learning-with-tab">
            <Tab value={1}>Popular Courses</Tab>
            <Tab value={2}>Certification Programs</Tab>
            <Tab value={3}>Skill-based Courses</Tab>
          </TabList>
          <Box className="hidden sm:block">
            <Box className="ml-6 flex gap-3">
              <Button
                color="primary"
                className={`responsive-slider-prev`}
                icon={<ChevronLeftOutline className={"w-5 h-5"} />}
                title="Previous"
              />
              <Button
                color="primary"
                className={`responsive-slider-next`}
                icon={<ChevronRightOutline className={"w-5 h-5"} />}
                title="Next"
                disabled
              />
            </Box>
          </Box>
        </Box>
        <TabPanel value={1}>
          <Courses
            data={data?.startLearningWithSpoclearn}
            tag="Popular Course"
          />
        </TabPanel>
        <TabPanel value={2}>
          <RenderIf
            condition={!loading}
            fallback={<StartLearningWithSpoclearnLoader />}
          >
            <Courses
              data={clientData?.serviceByType}
              tag="Certification Program"
            />
          </RenderIf>
        </TabPanel>
        <TabPanel value={3}>
          <RenderIf
            condition={!loading}
            fallback={<StartLearningWithSpoclearnLoader />}
          >
            <Courses
              data={clientData?.serviceByType}
              tag="Skill-based Course"
            />
          </RenderIf>
        </TabPanel>
      </Tabs>
    </Section>
  );
};

export default StartLearnWithSpoclearn;
